// edit-notification-settings.component.ts
import {Component, Inject} from '@angular/core';
import {DialogBaseComponent} from "../dialog-base/dialog-base.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {GameService} from "../../../services/game.service";
import {ActiveUserService} from "../../../services/active-user.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatError, MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {NgIf} from "@angular/common";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {DateService} from "../../../services/date.service";
import {IUpdateNotificationSettings} from "../../../interfaces/IUpdateNotificationSettings";

@Component({
  selector: 'app-edit-notification-settings',
  standalone: true,
  imports: [
    DialogBaseComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    ReactiveFormsModule,
    MatButton,
    NgIf
  ],
  templateUrl: './edit-notification-settings.component.html',
  styleUrl: './edit-notification-settings.component.css'
})
export class EditNotificationSettingsComponent {

  public uiState = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;

  private militaryTime: RegExp = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

  public notificationsBeginTimeControl = new FormControl<string | null>(null, [Validators.required, Validators.pattern(this.militaryTime)]);
  public notificationsEndTimeControl = new FormControl<string | null>(null, [Validators.required, Validators.pattern(this.militaryTime)]);
  public notificationFrequencyControl = new FormControl<number | null>(3600, [Validators.required, Validators.min(1)]);
  public notificationChunkSizeControl = new FormControl<number | null>(100, [Validators.required, Validators.min(1)]);

  public notificationSettingsForm = new FormGroup({
    notificationsBeginTime: this.notificationsBeginTimeControl,
    notificationsEndTime: this.notificationsEndTimeControl,
    notificationFrequency: this.notificationFrequencyControl,
    notificationChunkSize: this.notificationChunkSizeControl
  });

  constructor(private dialogRef: MatDialogRef<EditNotificationSettingsComponent>,
              private gameService: GameService,
              private activeUserService: ActiveUserService,
              private dateService: DateService,
              @Inject(MAT_DIALOG_DATA) public game: IGameQueryResult,
              private snackBarService: SnackbarService) {

    // Calculate actual times for notifications
    const drawTime = this.game.DrawTimeMinutesFromMidnightLocal;

    if (this.game.NotificationsBeginMinutesBeforeDraw !== undefined) {
      const beginTime = this.dateService.convertToMilitaryTime(drawTime - this.game.NotificationsBeginMinutesBeforeDraw);
      this.notificationsBeginTimeControl.setValue(beginTime);
    }

    if (this.game.NotificationsEndMinutesBeforeDraw !== undefined) {
      const endTime = this.dateService.convertToMilitaryTime(drawTime - this.game.NotificationsEndMinutesBeforeDraw);
      this.notificationsEndTimeControl.setValue(endTime);
    }

    this.notificationFrequencyControl.setValue(this.game.NotificationFrequency);
    this.notificationChunkSizeControl.setValue(this.game.NotificationChunkSize);
  }

  submit() {
    if (this.notificationSettingsForm.invalid) {
      this.notificationSettingsForm.markAllAsTouched();
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    const drawTime = this.game.DrawTimeMinutesFromMidnightLocal;
    const beginTime = this.dateService.convertMilitaryTimeToMinutes(this.notificationsBeginTimeControl.value!);
    const endTime = this.dateService.convertMilitaryTimeToMinutes(this.notificationsEndTimeControl.value!);

    let command: IUpdateNotificationSettings = {
      AdminId: this.activeUserService.activeUser().Id,
      GameId: this.game.Id,
      NotificationsBeginMinutesBeforeDraw: drawTime - beginTime,
      NotificationsEndMinutesBeforeDraw: drawTime - endTime,
      NotificationFrequency: this.notificationFrequencyControl.value!,
      NotificationChunkSize: this.notificationChunkSizeControl.value!
    };

    this.gameService.updateGameNotificationSettings(command).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackBarService.openSuccessfulSnackBar("Successfully updated notification settings!");
        this.dialogRef.close(true);
      },
      error: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackBarService.openErrorSnackBar("Failed to update notification settings!");
      }
    });
  }
}
